import React, { useEffect, useRef } from 'react';
import { WORLD_MASK, MASK_LAT_TOP, MASK_LAT_BOTTOM } from '../data/worldMask';
import { OFFICES, ARCS } from './Globe';

const MW = WORLD_MASK[0].length, MH = WORLD_MASK.length;

// World map drawn entirely from 0s and 1s. Digits flicker, a scan wave sweeps across,
// the pointer lights up nearby bits, and the three offices pulse red and link up.
const BinaryMap = ({ className = '' }) => {
  const cv = useRef(null);

  useEffect(() => {
    const canvas = cv.current, ctx = canvas.getContext('2d');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let w = 0, h = 0, cell = 12, cells = [], pins = [], raf, visible = true, last = 0;
    const ptr = { x: -999, y: -999 };

    const layout = () => {
      const r = canvas.getBoundingClientRect(), d = Math.min(window.devicePixelRatio || 1, 2);
      w = r.width; h = r.height; canvas.width = w * d; canvas.height = h * d; ctx.setTransform(d, 0, 0, d, 0, 0);
      cell = Math.max(9, Math.min(14, w / 130));
      const ratio = 360 / (MASK_LAT_TOP - MASK_LAT_BOTTOM);
      // desktop: whole world, New York at 40% and Sydney at 95% of the width; mobile: crop to Africa/Europe
      const mapW = w < 800 ? h * 0.92 * ratio : (0.55 * w * 360) / 226, mapH = mapW / ratio;
      const left = w < 800 ? w * 0.45 - 0.556 * mapW : 0.4 * w - 0.2944 * mapW, top = w < 800 ? h * 0.04 : (h - mapH) / 2 + 20;
      const toXY = (lat, lon) => [left + ((lon + 180) / 360) * mapW, top + ((MASK_LAT_TOP - lat) / (MASK_LAT_TOP - MASK_LAT_BOTTOM)) * mapH];
      cells = [];
      for (let y = cell / 2; y < h; y += cell) {
        for (let x = cell / 2; x < w; x += cell) {
          const u = (x - left) / mapW, v = (y - top) / mapH;
          if (u < 0 || u >= 1 || v < 0 || v >= 1) continue;
          if (WORLD_MASK[Math.floor(v * MH)][Math.floor(u * MW)] === '1') cells.push({ x, y, b: Math.random() < 0.5 ? 1 : 0 });
        }
      }
      pins = OFFICES.map((o) => ({ ...o, pos: toXY(o.lat, o.lon) }));
    };

    const ro = new ResizeObserver(layout); ro.observe(canvas); layout();
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; }, { threshold: 0 }); io.observe(canvas);
    const onMove = (e) => { const r = canvas.getBoundingClientRect(); ptr.x = e.clientX - r.left; ptr.y = e.clientY - r.top; };
    const onLeave = () => { ptr.x = ptr.y = -999; };
    const host = canvas.parentElement;
    host.addEventListener('pointermove', onMove); host.addEventListener('pointerleave', onLeave);

    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      if (!visible || now - last < 40) return; // ~25fps keeps it light
      last = now;
      const cs = getComputedStyle(document.documentElement);
      const paper = cs.getPropertyValue('--paper').trim(), red = cs.getPropertyValue('--red').trim();
      const dark = document.documentElement.dataset.theme === 'dark';
      const base = dark ? 0.34 : 0.3;
      const waveX = ((now / 9000) % 1.3) * (w + 400) - 200;

      ctx.clearRect(0, 0, w, h);
      ctx.font = `500 ${cell}px "JetBrains Mono", monospace`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';

      for (const c of cells) {
        if (!reduce && Math.random() < 0.004) c.b ^= 1;
        const dw = Math.abs(c.x - waveX), dp = Math.hypot(c.x - ptr.x, c.y - ptr.y);
        const wave = dw < 90 ? 1 - dw / 90 : 0, near = dp < 130 ? 1 - dp / 130 : 0;
        const hot = Math.max(wave * 0.8, near);
        if (hot > 0.08) { ctx.fillStyle = red; ctx.globalAlpha = Math.min(1, base + hot); }
        else { ctx.fillStyle = paper; ctx.globalAlpha = base; }
        ctx.fillText(near > 0.5 && !reduce && Math.random() < 0.3 ? (c.b ^ 1) : c.b, c.x, c.y);
      }

      // links between offices with a travelling bit
      const t = (now / 3500) % 1;
      ctx.lineWidth = 1; ctx.strokeStyle = red; ctx.fillStyle = red;
      ARCS.forEach(([a, b], k) => {
        const [x1, y1] = pins[a].pos, [x2, y2] = pins[b].pos;
        const cx = (x1 + x2) / 2 - (y2 - y1) * 0.25, cy = (y1 + y2) / 2 + (x2 - x1) * 0.25 - 60;
        ctx.globalAlpha = 0.5; ctx.setLineDash([3, 5]); ctx.beginPath(); ctx.moveTo(x1, y1); ctx.quadraticCurveTo(cx, cy, x2, y2); ctx.stroke(); ctx.setLineDash([]);
        const f = (t + k * 0.33) % 1, m = 1 - f;
        ctx.globalAlpha = 1; ctx.beginPath(); ctx.arc(m * m * x1 + 2 * m * f * cx + f * f * x2, m * m * y1 + 2 * m * f * cy + f * f * y2, 3, 0, 7); ctx.fill();
      });

      pins.forEach((p) => {
        const [x, y] = p.pos, ph = (now / 1700) % 1;
        ctx.strokeStyle = red; ctx.globalAlpha = (1 - ph) * 0.9; ctx.beginPath(); ctx.arc(x, y, 6 + ph * 26, 0, 7); ctx.stroke();
        ctx.globalAlpha = 1; ctx.fillStyle = red; ctx.beginPath(); ctx.arc(x, y, 5, 0, 7); ctx.fill();
        ctx.fillStyle = paper; ctx.font = '500 11px "JetBrains Mono", monospace';
        const ink = cs.getPropertyValue('--ink').trim(); ctx.lineWidth = 4; ctx.lineJoin = 'round'; ctx.strokeStyle = ink; const lx = p.side === 'l' ? x - 14 : x + 14; ctx.textAlign = p.side === 'l' ? 'right' : 'left'; ctx.strokeText(p.city.toUpperCase(), lx, y + 1); ctx.fillText(p.city.toUpperCase(), lx, y + 1); ctx.textAlign = 'center';
      });
      ctx.globalAlpha = 1;
    };
    raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); io.disconnect(); host.removeEventListener('pointermove', onMove); host.removeEventListener('pointerleave', onLeave); };
  }, []);

  return <canvas ref={cv} className={className} role="img" aria-label="World map made of binary digits, with enseQure offices in Harare, Johannesburg and Prague" />;
};

export default BinaryMap;
